import React, { SetStateAction, useState, useEffect } from 'react';

import useAlert from '../../Alerts/useAlert'
import { useHttpUtils } from '../../Utilities/useHttpUtils';
import { RequiredDialogInput, RequiredDialogSelect, DialogCheckbox } from '../../DialogInputs';
import CommonConfigDialog from './CommonConfigDialog';
import { handleInputChange } from '../../Utilities/DialogUtils';
import { Recipe } from '../../Types/Recipe';
import { PackageType } from '../../Types/PackageType';
import { RecipeToPackageType } from '../../Types/RecipeToPackageType';
import { useRecipes, usePackageTypes } from '../../Utilities/Hooks'

interface RecipeToPackageTypeEditDialogProps {
    dialogData: RecipeToPackageType,
    setDialogData: React.Dispatch<SetStateAction<RecipeToPackageType>>,
    showDialog: boolean,
    closeDialog: Function,
    businessUnitId: number,
    updateRecipeDefaultPackageCallback?: (packageTypeId: number, value: boolean) => void
}

export function RecipeToPackageTypeEditDialog({ dialogData, setDialogData, showDialog, closeDialog, updateRecipeDefaultPackageCallback }: RecipeToPackageTypeEditDialogProps) {
    const { fetchDataAuthenticated } = useHttpUtils();
    const [fieldErrors, setFieldErrors] = useState<Partial<RecipeToPackageType>>({});
    const { setAlert } = useAlert();
    const [refreshToken, setRefreshToken] = useState(0);
    const recipes: Recipe[] = useRecipes(refreshToken);
    const packageTypes: PackageType[] = usePackageTypes(refreshToken);

    const [isDefaultCheck, setIsDefaultCheck] = useState<boolean>(!!dialogData.isDefault);

    useEffect(() => {
        setIsDefaultCheck(dialogData.isDefault)
        if (showDialog) {
            setRefreshToken(r => r + 1);
        }
    }, [showDialog])

    const savePackageType = (recipeToPackageType: RecipeToPackageType) => {

        if (updateRecipeDefaultPackageCallback && dialogData.id && (isDefaultCheck !== dialogData.isDefault))
        {
            updateRecipeDefaultPackageCallback(dialogData.id, isDefaultCheck)
        } 

        let promise = new Promise((resolve, reject) => {
            let url = `api/v1/recipeToPackageType`;
            if (recipeToPackageType.id != null) {
                url += `/${recipeToPackageType.id}`
            }

            fetchDataAuthenticated(url, "POST", JSON.stringify(recipeToPackageType))
                .then(response => {
                    if (!response.ok) {
                        reject(response);
                        response.text().then(error => {
                            console.log(error)
                            setAlert("danger", "Failed to delete", !!error ? error : "An unexpected error occured")
                        })
                    } else {
                        setAlert("success", "Updated", "Recipe To Package Type has successfully been updated");
                    }
                    resolve(response);
                })
                .catch(error => {
                    alert("There was a problem! " + error);
                });
        });
        return promise;
    }

    const deletePackageType = (recipeToPackageType: RecipeToPackageType) => {
        let promise = new Promise((resolve, reject) => {
            let url = `api/v1/recipeToPackageType/${recipeToPackageType.id}`;

            fetchDataAuthenticated(url, "DELETE")
                .then(response => {
                    if (!response.ok) {
                        reject(response);
                        response.text().then(error => {
                            console.log(error)
                            setAlert("danger", "Failed to delete", !!error ? error : "An unexpected error occured")
                        })
                    } else {
                        setAlert("success", "Deleted", "Recipe To Package Type has successfully been deleted");
                    }
                    resolve(response);
                })
                .catch(error => {
                    alert("There was a problem! " + error);
                });
        });
        return promise;
    }

    const onChange = (e: any) => {
        handleInputChange(e, dialogData, setDialogData, fieldErrors, setFieldErrors);
    }

    const onIsDefaultCheckChange = (e: any) => {
        setIsDefaultCheck(e.target.checked);  
    }

    const PackageTypeAllowedForRecipe = (packageType: PackageType) => {
        if (dialogData.recipeId === null || dialogData.recipeId === 0)
            return true;

        if (recipes.length === 0)
            return false;

        let r = recipes.find((r) => r.recipeId == dialogData.recipeId);

        if (r && r.businessUnitId === packageType.businessUnitId)
            return true;

        return false;
    }

    const RecipeAllowedForPackageType = (recipe: Recipe) => {
        if (dialogData.packageTypeId === null || dialogData.packageTypeId === 0)
            return true;

        if (packageTypes.length === 0)
            return false;

        let pt = packageTypes.find((pt) => pt.id == dialogData.packageTypeId);

        if (pt && pt.businessUnitId === recipe.businessUnitId)
            return true;

        return false;
    }

    return (
        <CommonConfigDialog title={"Recipe To Package Type - " + dialogData.materialCode} dialogData={dialogData} showDialog={showDialog} closeDialog={closeDialog} save={savePackageType} fieldErrors={fieldErrors} setFieldErrors={setFieldErrors} wideDialog canDelete deleteFunction={deletePackageType}>
            <RequiredDialogInput controlId="formRecipeToPackageTypeMaterialCode" title="Material Code" placeholder="Material Code" name="materialCode" value={dialogData.materialCode} error={fieldErrors.materialCode} onChange={onChange} />
            <RequiredDialogSelect controlId="formRecipeToPackageTypePackageTypeId" title="Package Type" name="packageTypeId" value={dialogData.packageTypeId} error={fieldErrors.packageTypeId} onChange={onChange}>
                <option value="" key={0} >Please Select...</option>
                {
                    packageTypes
                        .filter((a) => PackageTypeAllowedForRecipe(a))
                        .sort((a, b) => (a.name.localeCompare(b.name)))
                        .map((packageType) =>
                            <option value={packageType.id ?? 0} key={packageType.id}>
                                {packageType.name}{!packageType.alternativeName ? "" : " (" + packageType.alternativeName+")"}
                            </option>
                    )
                }
            </RequiredDialogSelect>
            <RequiredDialogSelect controlId="formRecipeToPackageTypeRecipeId" title="Recipe" name="recipeId" value={dialogData.recipeId} error={fieldErrors.recipeId} onChange={onChange}>
                <option value="" key={0} >Please Select...</option>
                {recipes.filter((a) => RecipeAllowedForPackageType(a)).sort((a, b) => (a.name.localeCompare(b.name))).map((recipe) => <option value={recipe.recipeId} key={recipe.recipeId}>{recipe.name}</option>)}
            </RequiredDialogSelect>
            {
                updateRecipeDefaultPackageCallback && (
                    <DialogCheckbox controlId="formRecipeToPackageTypeIsDefault" title="Is Default" disabled={false} checked={isDefaultCheck} name="isDefault" onChange={onIsDefaultCheckChange} error={undefined} />
                )
            }
            
        </CommonConfigDialog>
    );
}